import React, { useEffect, useState } from 'react';
import { X, Plus, Trash2, Loader2, Pencil, Lock } from 'lucide-react';
import { jobApplicantListValueApi } from '../services/api';
import type { JobApplicantLovCategory, JobApplicantListValue } from '../types';
import { appConfirm } from '../context/SystemDialogContext';

export const JOB_APPLICANT_LOV_LABELS: Record<JobApplicantLovCategory, string> = {
  applying_for: 'Applying for',
  source_of_application: 'Source of application',
  initial_screen_passed: 'Initial screen passed?',
  invited_for_interview: 'Invited for interview?',
  application_status: 'Application status',
  reason_for_decision: 'Reason for decision',
};

interface JobApplicantLovManageModalProps {
  open: boolean;
  category: JobApplicantLovCategory;
  onClose: () => void;
  onChanged?: () => void;
}

function apiErrorMessage(err: unknown, fallback: string): string {
  return (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? fallback;
}

export const JobApplicantLovManageModal: React.FC<JobApplicantLovManageModalProps> = ({
  open,
  category,
  onClose,
  onChanged,
}) => {
  const [values, setValues] = useState<JobApplicantListValue[]>([]);
  const [loading, setLoading] = useState(false);
  const [newValue, setNewValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editValue, setEditValue] = useState('');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await jobApplicantListValueApi.getAll(category);
      setValues(rows);
    } catch {
      setError('Failed to load list values.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setNewValue('');
      setError(null);
      setEditingId(null);
      setEditValue('');
      void load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, category]);

  const handleAdd = async () => {
    const trimmed = newValue.trim();
    if (!trimmed) return;
    setSaving(true);
    setError(null);
    try {
      await jobApplicantListValueApi.create({
        category,
        value: trimmed,
        displayOrder: values.length + 1,
      });
      setNewValue('');
      await load();
      onChanged?.();
    } catch (err: unknown) {
      setError(apiErrorMessage(err, 'Failed to add value.'));
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (item: JobApplicantListValue) => {
    if (item.inUse) return;
    setEditingId(item.id);
    setEditValue(item.value);
    setError(null);
  };

  const handleSaveEdit = async (item: JobApplicantListValue) => {
    const trimmed = editValue.trim();
    if (!trimmed) return;
    if (trimmed === item.value) {
      setEditingId(null);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await jobApplicantListValueApi.update(item.id, {
        category,
        value: trimmed,
        displayOrder: item.displayOrder,
      });
      setEditingId(null);
      await load();
      onChanged?.();
    } catch (err: unknown) {
      setError(apiErrorMessage(err, 'Failed to update value.'));
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (item: JobApplicantListValue) => {
    if (item.inUse) return;
    if (!(await appConfirm(`Remove "${item.value}" from the list?`, { danger: true, confirmLabel: 'Remove' }))) return;
    setSaving(true);
    setError(null);
    try {
      await jobApplicantListValueApi.delete(item.id);
      await load();
      onChanged?.();
    } catch (err: unknown) {
      setError(apiErrorMessage(err, 'Failed to remove value.'));
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h3 className="text-lg font-semibold text-neutral-800">
            Manage {JOB_APPLICANT_LOV_LABELS[category]}
          </h3>
          <button type="button" onClick={onClose} className="rounded p-1 hover:bg-neutral-100">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="flex gap-2">
            <input
              type="text"
              value={newValue}
              onChange={(e) => setNewValue(e.target.value)}
              placeholder="New list value"
              className="flex-1 rounded-md border border-neutral-300 px-3 py-2 text-sm"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void handleAdd();
                }
              }}
            />
            <button
              type="button"
              onClick={() => void handleAdd()}
              disabled={saving || !newValue.trim()}
              className="inline-flex items-center gap-1 rounded-md bg-blue-600 px-3 py-2 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              Add
            </button>
          </div>

          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
            </div>
          ) : (
            <ul className="max-h-64 space-y-1 overflow-y-auto">
              {values.length === 0 && (
                <li className="py-4 text-center text-sm text-neutral-500">No values yet.</li>
              )}
              {values.map((item) => {
                const inUse = !!item.inUse;
                const isEditing = editingId === item.id;
                const lockTitle = inUse
                  ? `Assigned to ${item.usageCount ?? 0} applicant(s) — cannot modify or delete`
                  : undefined;
                return (
                  <li
                    key={item.id}
                    className="flex items-center justify-between gap-2 rounded-md border border-neutral-200 px-3 py-2 text-sm"
                  >
                    {isEditing ? (
                      <input
                        type="text"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        className="min-w-0 flex-1 rounded border border-neutral-300 px-2 py-1 text-sm"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            void handleSaveEdit(item);
                          }
                          if (e.key === 'Escape') setEditingId(null);
                        }}
                      />
                    ) : (
                      <span className="min-w-0 flex-1">
                        {item.value}
                        {inUse && (
                          <span className="ml-2 text-xs text-amber-700">
                            In use ({item.usageCount})
                          </span>
                        )}
                      </span>
                    )}
                    <div className="flex shrink-0 items-center gap-0.5">
                      {isEditing ? (
                        <>
                          <button
                            type="button"
                            onClick={() => void handleSaveEdit(item)}
                            disabled={saving || !editValue.trim()}
                            className="rounded px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-50 disabled:opacity-50"
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            onClick={() => setEditingId(null)}
                            className="rounded px-2 py-1 text-xs text-neutral-600 hover:bg-neutral-100"
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={() => startEdit(item)}
                            disabled={saving || inUse}
                            className="rounded p-1 text-neutral-600 hover:bg-neutral-100 disabled:cursor-not-allowed disabled:opacity-40"
                            title={lockTitle ?? 'Modify value'}
                          >
                            {inUse ? <Lock className="h-4 w-4" /> : <Pencil className="h-4 w-4" />}
                          </button>
                          <button
                            type="button"
                            onClick={() => void handleRemove(item)}
                            disabled={saving || inUse}
                            className="rounded p-1 text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40"
                            title={lockTitle ?? 'Remove value'}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          <p className="text-xs text-neutral-500">
            Options assigned to an applicant cannot be modified or deleted.
          </p>
        </div>
      </div>
    </div>
  );
};
